/* rbac/roles — role definitions, hierarchy, and capability matrix.
   The effective role is derived from the backend session (effectiveRole field)
   and normalised via roleFromSession(). RbacContext is the authoritative source.

   Four roles, hierarchical:
     Super Admin  > Organization Admin > Workspace Admin > Workspace User
*/

export const ROLES = {
  SUPER_ADMIN: 'Super Admin',
  ORG_ADMIN: 'Organization Admin',
  WS_ADMIN: 'Workspace Admin',
  WS_USER: 'Workspace User',
};

export const ROLE_ORDER = [ROLES.SUPER_ADMIN, ROLES.ORG_ADMIN, ROLES.WS_ADMIN, ROLES.WS_USER];

export const ROLE_META = {
  [ROLES.SUPER_ADMIN]: { tone: 'purple', icon: 'Shield',  scope: 'Platform',     desc: 'Full access to every tenant, license, partner, and audit trail.' },
  [ROLES.ORG_ADMIN]:   { tone: 'blue',   icon: 'Building2',scope: 'Organization', desc: 'Manages one organization: workspaces, users, settings, branding.' },
  [ROLES.WS_ADMIN]:    { tone: 'green',  icon: 'LayoutGrid',scope: 'Workspace',   desc: 'Manages a workspace: membership, settings, and CRM data.' },
  [ROLES.WS_USER]:     { tone: 'slate',  icon: 'User',     scope: 'Workspace',    desc: 'Uses the CRM within assigned workspaces. No admin access.' },
};

/* rank: lower index = more privilege. Super Admin (0) satisfies everything. */
export const roleRank = (role) => {
  const i = ROLE_ORDER.indexOf(role);
  return i === -1 ? ROLE_ORDER.length : i;
};

/* atLeast(current, required): does `current` meet/exceed the `required` role? */
export const atLeast = (current, required) => roleRank(current) <= roleRank(required);

/* Capability matrix — action → minimum role required.
   Used by `can(role, action)` and by route guards / nav filters. */
export const CAPABILITIES = {
  'platform.view':        ROLES.SUPER_ADMIN,
  'platform.manage':      ROLES.SUPER_ADMIN,
  'org.view':             ROLES.ORG_ADMIN,
  'org.manage':           ROLES.ORG_ADMIN,
  'org.branding':         ROLES.ORG_ADMIN,
  'workspace.create':     ROLES.ORG_ADMIN,
  'workspace.delete':     ROLES.ORG_ADMIN,
  'workspace.view':       ROLES.WS_USER,
  'workspace.settings':   ROLES.WS_ADMIN,
  'membership.view':      ROLES.WS_USER,
  'membership.manage':    ROLES.WS_ADMIN,
  'workspace.assign':     ROLES.ORG_ADMIN,
  'crm.use':              ROLES.WS_USER,
  'crm.manage':           ROLES.WS_ADMIN,
};

export const can = (role, action) => {
  const required = CAPABILITIES[action];
  if (!required) return false;
  return atLeast(role, required);
};

/* Map an auth-session role string → an RBAC role (best-effort default).
   Handles both frontend display names and backend SCREAMING_SNAKE_CASE enums. */
export const roleFromSession = (sessionRole) => {
  switch (sessionRole) {
    case ROLES.SUPER_ADMIN:
    case 'SUPER_ADMIN':
    case 'Super Admin':
      return ROLES.SUPER_ADMIN;
    case ROLES.ORG_ADMIN:
    case 'ORG_ADMIN':
    case 'Admin':
    case 'Org Admin':
      return ROLES.ORG_ADMIN;
    case ROLES.WS_ADMIN:
    case 'WORKSPACE_ADMIN':
    case 'Sales Lead':
    case 'Workspace Admin':
      return ROLES.WS_ADMIN;
    case ROLES.WS_USER:
    case 'WORKSPACE_USER':
    case 'AE':
    case 'Workspace User':
    case 'Member':
      return ROLES.WS_USER;
    default:
      return ROLES.WS_USER;
  }
};
